import React, { useState } from 'react';
import { Plus, Trash2, Edit, Check, X, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useProjectCompliance } from '../../hooks/useCompliance';

const ComplianceChecklistPanel = ({ projectId, onUpdate, setActivePanel }) => {
    const {
        compliance,
        loading,
        toggleCompliance,
        addComplianceItem,
        updateComplianceItem,
        deleteComplianceItem,
        addApproval,
        refreshCompliance
    } = useProjectCompliance(projectId);
    
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [newItem, setNewItem] = useState({
        category: "",
        customCategory: "",
        item: ""
    });
    const [editItem, setEditItem] = useState({
        category: "",
        customCategory: "",
        item: ""
    });
    const [approvals, setApprovals] = useState({});

    // Function to notify parent component of compliance update
    const notifyComplianceUpdate = () => {
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('complianceUpdated'));
        if (onUpdate) onUpdate();
    };

    // Handle adding a new compliance item
    const handleAddItem = async () => {
        if (!newItem.item.trim()) {
            toast.error("Please enter a compliance item description");
            return;
        }
        
        const category = newItem.category === "Custom" ? newItem.customCategory : newItem.category;
        if (!category) {
            toast.error("Please select or enter a category");
            return;
        }
        
        try {
            await addComplianceItem({
                category: category,
                description: newItem.item
            });
            
            setNewItem({ category: "", customCategory: "", item: "" });
            setIsAdding(false);
            notifyComplianceUpdate();
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    // Handle editing a compliance item
    const handleEditItem = (itemId) => {
        const itemToEdit = compliance.complianceItems.find(item => item.id === itemId);
        if (itemToEdit && !itemToEdit.isGeneral) {
            setEditingId(itemId);
            setEditItem({
                category: itemToEdit.category,
                customCategory: itemToEdit.category,
                item: itemToEdit.item
            });
        }
    };

    // Handle updating a compliance item
    const handleUpdateItem = async () => {
        if (!editItem.item.trim()) {
            toast.error("Please enter a compliance item description");
            return;
        }
        
        const category = editItem.category === "Custom" ? editItem.customCategory : editItem.category;
        if (!category) {
            toast.error("Please select or enter a category");
            return;
        }
        
        try {
            await updateComplianceItem(editingId, {
                category: category,
                item: editItem.item
            });
            
            setEditingId(null);
            notifyComplianceUpdate();
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    // Handle deleting a compliance item
    const handleDeleteItem = async (itemId) => {
        if (window.confirm("Are you sure you want to delete this compliance item?")) {
            try {
                await deleteComplianceItem(itemId);
                notifyComplianceUpdate();
            } catch (error) {
                // Error is already handled in the hook
            }
        }
    };

    // Handle approval
    const handleApproval = async (itemId, status, comment) => {
        try {
            await addApproval(itemId, { status, comment });
            setApprovals(prev => ({
                ...prev,
                [itemId]: { status, comment, date: new Date().toISOString() }
            }));
            notifyComplianceUpdate();
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    // Handle toggling compliance status
    const handleToggleCompliance = async (itemId) => {
        try {
            await toggleCompliance(itemId);
            notifyComplianceUpdate();
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    // Handle refreshing compliance items
    const handleRefreshCompliance = async () => {
        try {
            await refreshCompliance();
            notifyComplianceUpdate();
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    // Group items by category
    const groupedItems = compliance?.complianceItems?.reduce((acc, item) => {
        if (!acc[item.category]) {
            acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
    }, {}) || {};

    if (loading) return <p className="text-center py-4 text-gray-500">Loading checklist...</p>;

    const compliancePercentage = compliance?.compliancePercentage || 0;
    const complianceTrend = compliance?.complianceTrend || 0;

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">South Sudan NGO Compliance Requirements</h3>
                <p className="text-sm text-blue-700">
                    Ensure all projects comply with South Sudan's regulatory requirements and organizational policies.
                    Check off completed requirements and add project-specific policies as needed.
                </p>
            </div>
            
            {/* Overall Compliance Status with Trend */}
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-orange-800">Overall Compliance Status</h4>
                    <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 mr-2">
                            {compliance?.complianceItems?.filter(item => item.isComplete).length || 0} of {compliance?.complianceItems?.length || 0} requirements met
                        </span>
                        {complianceTrend !== 0 && (
                            <div className={`flex items-center ${complianceTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {complianceTrend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                <span className="ml-1 text-sm font-medium">{Math.abs(complianceTrend)}%</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                            className={`h-3 rounded-full transition-all duration-500 ${
                                compliancePercentage > 80 ? 'bg-green-600' : 
                                compliancePercentage > 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${compliancePercentage}%` }}
                        ></div>
                    </div>
                    <span className="ml-3 text-sm font-medium text-gray-700 min-w-[50px]">
                        {compliancePercentage}%
                    </span>
                </div>
                <div className="mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        compliancePercentage > 80 ? 'bg-green-100 text-green-800' : 
                        compliancePercentage > 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                        {compliancePercentage > 80 ? 'High Compliance' : 
                         compliancePercentage > 60 ? 'Medium Compliance' : 'Low Compliance'}
                    </span>
                </div>
            </div>
            
            <div className="flex justify-between items-center">
                <h4 className="font-semibold text-gray-800">Compliance Checklist</h4>
                <div className="flex space-x-2">
                    <button
                        onClick={handleRefreshCompliance}
                        className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                    >
                        Refresh Policies
                    </button>
                    <button
                        onClick={() => setIsAdding(!isAdding)}
                        className="flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                    >
                        <Plus size={16} className="mr-1" /> Add Policy
                    </button>
                </div>
            </div>
            
            {/* Add New Item Form */}
            {isAdding && (
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="font-medium text-gray-800 mb-3">Add New Compliance Policy</h4>
                    
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={newItem.category}
                                onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            >
                                <option value="">Select a category</option>
                                <option value="Legal">Legal</option>
                                <option value="Financial">Financial</option>
                                <option value="Operational">Operational</option>
                                <option value="HR">HR</option>
                                <option value="Reporting">Reporting</option>
                                <option value="Donor Compliance">Donor Compliance</option>
                                <option value="Safeguarding">Safeguarding</option>
                                <option value="Anti-Fraud">Anti-Fraud</option>
                                <option value="Environmental">Environmental</option>
                                <option value="Social">Social</option>
                                <option value="M&E">M&E</option>
                                <option value="Custom">Custom Category</option>
                            </select>
                        </div>
                        
                        {newItem.category === "Custom" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Category Name</label>
                                <input
                                    type="text"
                                    value={newItem.customCategory}
                                    onChange={(e) => setNewItem({...newItem, customCategory: e.target.value})}
                                    placeholder="Enter category name"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                />
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Policy Description</label>
                            <textarea
                                value={newItem.item}
                                onChange={(e) => setNewItem({...newItem, item: e.target.value})}
                                placeholder="Describe the compliance requirement or policy"
                                rows="3"
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setIsAdding(false)}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddItem}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                            >
                                Add Policy
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Edit Item Form */}
            {editingId && (
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="font-medium text-gray-800 mb-3">Edit Compliance Policy</h4>
                    
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={editItem.category}
                                onChange={(e) => setEditItem({...editItem, category: e.target.value})}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            >
                                <option value="Legal">Legal</option>
                                <option value="Financial">Financial</option>
                                <option value="Operational">Operational</option>
                                <option value="HR">HR</option>
                                <option value="Reporting">Reporting</option>
                                <option value="Donor Compliance">Donor Compliance</option>
                                <option value="Safeguarding">Safeguarding</option>
                                <option value="Anti-Fraud">Anti-Fraud</option>
                                <option value="Environmental">Environmental</option>
                                <option value="Social">Social</option>
                                <option value="M&E">M&E</option>
                                <option value="Custom">Custom Category</option>
                            </select>
                        </div>
                        
                        {editItem.category === "Custom" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Category Name</label>
                                <input
                                    type="text"
                                    value={editItem.customCategory}
                                    onChange={(e) => setEditItem({...editItem, customCategory: e.target.value})}
                                    placeholder="Enter category name"
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                />
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Policy Description</label>
                            <textarea
                                value={editItem.item}
                                onChange={(e) => setEditItem({...editItem, item: e.target.value})}
                                placeholder="Describe the compliance requirement or policy"
                                rows="3"
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateItem}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                            >
                                Update Policy
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Compliance Items by Category */}
            <div className="space-y-4">
                {Object.entries(groupedItems).map(([category, items]) => (
                    <div key={category} className="space-y-3">
                        <div className="flex justify-between items-center">
                            <h4 className="font-semibold text-lg text-gray-800 border-b pb-1">{category} Compliance</h4>
                            <span className="text-sm text-gray-500">
                                {items.filter(item => item.isComplete).length} of {items.length} completed
                            </span>
                        </div>
                        
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className={`p-3 rounded-lg border ${item.isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'} shadow-sm transition-all`}
                            >
                                <div className="flex items-start">
                                    {/* Compliance Toggle */}
                                    <div className="flex-shrink-0 mr-3 mt-1">
                                        <input
                                            type="checkbox"
                                            checked={item.isComplete}
                                            onChange={() => handleToggleCompliance(item.id)}
                                            className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                                        />
                                    </div>
                                    
                                    <div className="flex-1">
                                        <span className={`block ${item.isComplete ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                                            {item.item}
                                        </span>
                                        {item.isGeneral && (
                                            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                                                General Policy
                                            </span>
                                        )}
                                        
                                        {/* Link to procurement panel for procurement-related items */}
                                        {item.item.toLowerCase().includes("procurement") && (
                                            <button
                                                onClick={() => {
                                                    setActivePanel("budget");
                                                    toast.info("Opening procurement plan details");
                                                }}
                                                className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                            >
                                                View Plan
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Edit/Delete Buttons for Custom Items */}
                                    {!item.isGeneral && (
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={() => handleEditItem(item.id)}
                                                className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                title="Edit Policy"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteItem(item.id)}
                                                className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                                title="Delete Policy"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Approval Workflow */}
                                <div className="mt-2 pt-2 border-t border-gray-100">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleApproval(item.id, "approved", "Meets all requirements")}
                                            className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 flex items-center"
                                        >
                                            <Check size={12} className="mr-1" /> Approve
                                        </button>
                                        <button
                                            onClick={() => handleApproval(item.id, "rejected", "Needs additional documentation")}
                                            className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200 flex items-center"
                                        >
                                            <X size={12} className="mr-1" /> Reject
                                        </button>
                                        <button
                                            onClick={() => handleApproval(item.id, "pending", "Requires further review")}
                                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 flex items-center"
                                        >
                                            <AlertCircle size={12} className="mr-1" /> Request Info
                                        </button>
                                    </div>
                                    
                                    {approvals[item.id] && (
                                        <div className="mt-2 text-xs">
                                            <span className={`font-medium ${
                                                approvals[item.id].status === 'approved' ? 'text-green-700' :
                                                approvals[item.id].status === 'rejected' ? 'text-red-700' : 'text-yellow-700'
                                            }`}>
                                                {approvals[item.id].status.toUpperCase()}:
                                            </span>
                                            <span className="text-gray-600 ml-1">{approvals[item.id].comment}</span>
                                            <div className="text-gray-500 mt-1">
                                                {new Date(approvals[item.id].date).toLocaleString()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ComplianceChecklistPanel;